import React, { useEffect, useRef, useState } from 'react';
import { devError } from '../lib/devLog';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PriceTag from '../components/PriceTag';
import { ArrowRight, BookOpen, Stamp, Sparkles, Layers, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SBM_ARTIST_ID = 'f1e7c91d-3d8c-4e3a-9a7e-6e2b8f0a1234';

// Hoisted animation presets — Framer Motion re-runs on every prop reference
// change, so keeping these module-level avoids allocating fresh objects for
// every card in every render pass.
const HOVER_LIFT = { scale: 1.04 };
const TAP_PRESS = { scale: 0.96 };
const FADE_IN_UP = { opacity: 0, y: 20 };
const FADE_IN = { opacity: 1, y: 0 };
const VIEWPORT_ONCE = { once: true };

const Section = ({ children, className = '', id = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  return (
    <motion.section
      id={id}
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Per-series frame aspect ratio (matches Gabrielle's spec sheet sizes).
// All cards within a series will be visually identical in size & shape.
//
// IMPORTANT: for bare prints (preFramed=false) the outer aspect must be
// WIDER than the print's true ratio to compensate for the wall + frame +
// mat padding (which subtracts ~18% of the outer width from each axis).
// Otherwise object-contain leaves uneven extra whitespace top/bottom and
// the visible mat looks uneven. Each ratio below is calibrated so the
// inner image area resolves to the print's true ratio after all padding.
const SERIES_RATIO = {
  marilyn_classic: 'aspect-square',                  // 36 x 36  (preFramed)
  flowers:         'aspect-square',                  // 36 x 36  (preFramed)
  mao:             'aspect-[9/10]',                  // print 29½ × 33½ (bare)
  soup_one:        'aspect-[3/4]',                   // print 35 × 23   (bare)
  soup_two:        'aspect-[3/4]',                   // print 35 × 23   (bare)
  golden_marilyn:  'aspect-square',                  // 36 x 36  (square print)
  diamond_dust:    'aspect-square',                  // 36 x 36  (square print)
  kiss:            'aspect-[10/13]',                 // print 16½ × 22½ (bare)
  dollar_signs:    'aspect-[15625/19750]',           // 15⅝ × 19¾ (preFramed)
  scream:          'aspect-[3/4]',                   // print 25 × 35   (bare)
  mona_lisa:       'aspect-[3/4]',                   // print 25 × 35   (bare)
  moonwalk:        'aspect-square',                  // 38½ × 38½ (square print)
  beethoven:       'aspect-square',                  // 38½ × 38½ (square print)
};

// Per-series CSS frame proportions for the bare-print render (when
// `preFramed` is false). Tuned so each card matches Gabrielle's printed
// reference framing for that series. Values are CSS % padding strings
// applied to nested containers: outer (wall around frame) → black frame
// thickness → inner mat (white border between frame and print).
//
// The Soup spec: generous off-white gallery wall + thin black moulding +
// a small uniform white mat that honours the print's own paper border.
// `padding: X%` in CSS always references the parent's WIDTH, so the same
// % value produces equal pixel margins on top/bottom and left/right —
// i.e. the white border is visually identical on every side of the print.
const DEFAULT_FRAME = {
  outer: '3%', frameThickness: '3.5%', mat: '3%', wall: 'bg-white',
};
const SOUP_FRAME = {
  outer: '15%',           // generous gallery-wall padding around the frame
  frameThickness: '1.6%', // thin black moulding
  mat: '3%',              // small uniform white mat — equal on all sides
                          // (CSS % padding references parent width, so
                          // vertical & horizontal pixel margins match)
  wall: 'bg-[#f3f2ee]',   // soft off-white gallery wall (matches reference)
};
const SERIES_FRAME = {
  soup_one: SOUP_FRAME,
  soup_two: SOUP_FRAME,
};

const EditionCard = ({ work, seriesRatio = 'aspect-square', seriesFrame = DEFAULT_FRAME, navigate }) => {
  // Build the image URL with a cache-busting query based on updatedAt so the
  // browser picks up image-file overwrites (e.g. when a colourway is remapped
  // to a different JPEG without renaming the path).
  const base = work.imageUrl?.startsWith('http') ? work.imageUrl : `${API_URL}${work.imageUrl}`;
  const stamp = work.updatedAt ? new Date(work.updatedAt).getTime() : '';
  const src = stamp ? `${base}${base.includes('?') ? '&' : '?'}v=${stamp}` : base;
  // `preFramed` images are already wall-shot photos with a real frame baked
  // into the JPEG (e.g. the Marilyn / Dollar Sign / Mona Lisa / Flowers
  // images Gabrielle supplies). They render edge-to-edge on a soft-grey
  // wall background. Bare prints (Smolensky CDN scrapes) get a matching
  // CSS frame chrome — thin black frame + white mat — so the page reads
  // uniformly framed-on-wall.
  const preFramed = !!work.preFramed;
  // Portfolio/set cards are NOT a single artwork — they're marketing tiles
  // showing the multiple prints in the series that can be bought together.
  // The source JPEG is a composite (often landscape) so wrapping it in a
  // single tall portrait CSS frame looks ridiculous. Display the composite
  // image as-is on a clean white card, no frame chrome.
  const isPortfolioSet = /\b(portfolio|piece set|set of \d|\d-piece)\b/i.test(work.title || '');
  return (
  <motion.button
    type="button"
    onClick={() => navigate(`/artwork/${work.slug || work.id}`)}
    whileHover={{ y: -4 }}
    transition={{ duration: 0.3 }}
    className={`group text-left w-full ${isPortfolioSet ? 'sm:col-span-2 md:col-span-2 lg:col-span-2' : ''}`}
    data-testid={`sbm-edition-card-${work.id}`}
  >
    {(preFramed || isPortfolioSet) ? (
      // Either: (a) the JPG already has the frame + wall baked in (preFramed
      // supplied by Gabrielle), or (b) it's a portfolio composite showing
      // multiple prints together. Either way, render the source image as-is
      // edge-to-edge on the card with no CSS frame chrome.
      //
      // Portfolio composites span TWO grid columns and use a landscape aspect
      // so the mini-frames within the composite read at a visual height
      // comparable to the individual single-print cards next to them.
      <div className={`${isPortfolioSet ? 'aspect-[2/1]' : seriesRatio} mb-4 overflow-hidden ${isPortfolioSet ? 'bg-white flex items-center justify-center' : ''}`}>
        <img
          src={src}
          alt={work.title}
          loading="lazy"
          className={`w-full h-full ${isPortfolioSet ? 'object-contain' : 'object-cover'} group-hover:scale-[1.02] transition-transform duration-700 ease-out`}
        />
      </div>
    ) : (
      // Bare print — wrap in CSS chrome matching Gabrielle's framed
      // reference for this series: gallery-wall background, slim black
      // moulding with a soft drop shadow, and the print fitting inside the
      // frame (with or without a mat depending on `seriesFrame.mat`).
      <div
        className={`${seriesRatio} mb-4 ${seriesFrame.wall || 'bg-white'} flex items-center justify-center overflow-hidden`}
        style={{ padding: seriesFrame.outer }}
      >
        <div
          className="w-full h-full bg-black relative"
          style={{
            padding: seriesFrame.frameThickness,
            boxShadow: '0 24px 38px -16px rgba(0,0,0,0.32), 0 8px 16px -8px rgba(0,0,0,0.22)',
          }}
        >
          {/* Inner layer: either a white mat with the print sitting on it,
              or — when mat is 0 — the print fills the frame directly.
              `object-cover` is correct when the print already matches the
              series aspect ratio (Soup); `object-contain` preserves shape
              when sitting on a mat. */}
          <div
            className="w-full h-full bg-white flex items-center justify-center overflow-hidden"
            style={{ padding: seriesFrame.mat || '0' }}
          >
            <img
              src={src}
              alt={work.title}
              loading="lazy"
              className={`w-full h-full ${seriesFrame.mat && seriesFrame.mat !== '0%' ? 'object-contain' : 'object-cover'} group-hover:scale-[1.02] transition-transform duration-700 ease-out`}
            />
          </div>
        </div>
      </div>
    )}
    <h3 className="text-base font-medium tracking-tight leading-snug">
      {work.title.replace(' (after Andy Warhol)', '').replace(' (after Edvard Munch / Andy Warhol)', '').replace(' (after Leonardo da Vinci / Andy Warhol)', '')}
    </h3>
    <div className="mt-1 flex items-baseline justify-between gap-3 text-sm text-neutral-500">
      <span className="truncate">{work.edition?.split(';')[0] || work.year}</span>
      <span className="font-medium text-black whitespace-nowrap">
        <PriceTag priceDisplay={work.priceDisplay} vatScheme={work.vatScheme || 'standard'} />
      </span>
    </div>
  </motion.button>
  );
};

const SeriesGrid = ({ id, title, kicker, works, seriesRatio, seriesFrame, navigate }) => {
  if (!works || !works.length) return null;
  return (
    <Section id={id} className="py-20 sm:py-28 border-t border-neutral-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12 max-w-3xl">
          <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">{kicker}</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight">{title}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
          {works.map((w) => (
            <EditionCard key={w.id} work={w} seriesRatio={seriesRatio} seriesFrame={seriesFrame} navigate={navigate} />
          ))}
        </div>
      </div>
    </Section>
  );
};

// ─── Sticky in-page series navigator ────────────────────────────────────────
// Pinned just below the global header. Horizontally scrollable on mobile.
// Highlights the active series via IntersectionObserver on each `#sbm-*`
// anchor, and clicks scroll-smooth-jump to that series.
const SHORT_LABEL = {
  marilyn_classic: 'Marilyn',
  flowers: 'Flowers',
  soup_one: 'Soup I',
  mao: 'Mao',
  golden_marilyn: 'Golden Marilyn',
  diamond_dust: 'Diamond Dust',
  kiss: 'Kiss',
  dollar_signs: 'Dollar Signs',
  soup_two: 'Soup II',
  scream: 'Scream',
  mona_lisa: 'Mona Lisa',
  moonwalk: 'Moonwalk',
  beethoven: 'Beethoven',
};

const SeriesNav = ({ sections, seriesGroups }) => {
  const available = sections.filter((s) => (seriesGroups[s.key] || []).length > 0);
  const [activeKey, setActiveKey] = useState(available[0]?.key);
  const containerRef = useRef(null);
  // Suppress observer-driven activeKey updates while a chip-click jump is
  // in progress (otherwise every section the smooth scroll passes through
  // briefly wins the race, ending on the first section).
  const programmaticUntilRef = useRef(0);

  useEffect(() => {
    if (!available.length) return undefined;
    const observers = available.map((s) => {
      const el = document.getElementById(`sbm-${s.key.replace(/_/g, '-')}`);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          if (Date.now() < programmaticUntilRef.current) return;
          setActiveKey(s.key);
        },
        { rootMargin: '-40% 0px -55% 0px', threshold: 0 },
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o && o.disconnect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available.length]);

  // Auto-scroll the chip strip horizontally so the active chip stays in view.
  // Manual scrollLeft (not scrollIntoView) — `block: 'nearest'` on a sticky
  // element can cause spurious vertical document scrolls.
  useEffect(() => {
    const strip = containerRef.current;
    if (!strip) return;
    const chip = strip.querySelector(`[data-series-key="${activeKey}"]`);
    if (!chip) return;
    const target = chip.offsetLeft - (strip.clientWidth / 2) + (chip.clientWidth / 2);
    strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [activeKey]);

  const handleJump = (key) => {
    const el = document.getElementById(`sbm-${key.replace(/_/g, '-')}`);
    if (!el) return;
    // Lock the active chip + freeze observer for the duration of the jump.
    programmaticUntilRef.current = Date.now() + 1200;
    setActiveKey(key);
    // Account for sticky header (≈64px mobile / ≈124px ≥lg) + chip strip (~52px).
    const offset = window.innerWidth >= 1024 ? 180 : 120;
    const y = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  if (!available.length) return null;

  return (
    <div
      className="sticky top-[64px] lg:top-[124px] z-30 bg-white/95 backdrop-blur border-y border-neutral-200"
      data-testid="sbm-series-nav"
    >
      <div
        ref={containerRef}
        className="max-w-7xl mx-auto flex gap-1 sm:gap-2 overflow-x-auto px-4 sm:px-6 py-3 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {available.map((s) => {
          const isActive = s.key === activeKey;
          return (
            <button
              key={s.key}
              data-series-key={s.key}
              data-testid={`sbm-nav-chip-${s.key}`}
              type="button"
              onClick={() => handleJump(s.key)}
              className={`shrink-0 px-3 sm:px-4 py-1.5 text-xs sm:text-[13px] tracking-wide whitespace-nowrap transition-colors duration-200 border ${
                isActive
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-neutral-700 border-neutral-200 hover:border-black hover:text-black'
              }`}
            >
              {SHORT_LABEL[s.key] || s.key}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const SundayBMorningPage = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.85]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchWorks = async () => {
      try {
        const res = await fetch(`${API_URL}/api/artworks?artist=${encodeURIComponent('Sunday B. Morning')}&limit=200`);
        const data = await res.json();
        const all = Array.isArray(data) ? data : [];
        // Strict match: only works that belong to the canonical Sunday B. Morning artist
        // (the API's string-match would otherwise include legacy Warhol entries that mention
        // "Sunday B." in their title)
        setWorks(all.filter((w) => w.artistId === SBM_ARTIST_ID));
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') devError('Sunday B. Morning fetch failed', e);
      } finally {
        setLoading(false);
      }
    };
    fetchWorks();
    // Run once on mount. `API_URL` is a module constant and never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group works by their `series` tag (set in the DB to match Gabrielle's spec).
  // Fallback to legacy detection so the page still renders if anything is untagged.
  const seriesGroups = works.reduce((acc, w) => {
    let s = w.series;
    if (!s) {
      if (/^Classic Marilyn|^Marilyn — 10-Print/.test(w.title)) s = 'marilyn_classic';
      else if (/^Flowers 11\./.test(w.title)) s = 'flowers';
      else if (/^Soup Can I /.test(w.title)) s = 'soup_one';
      else if (/^Mao —/.test(w.title)) s = 'mao';
      else if (/^Golden Marilyn/.test(w.title)) s = 'golden_marilyn';
      else if (/^Diamond Dust Marilyn/.test(w.title)) s = 'diamond_dust';
      else if (/^I Love Your Kiss/.test(w.title)) s = 'kiss';
      else if (/^Dollar Sign —/.test(w.title)) s = 'dollar_signs';
      else if (/^Soup Can II /.test(w.title)) s = 'soup_two';
      else if (/^The Scream/.test(w.title)) s = 'scream';
      else if (/^Mona Lisa/.test(w.title)) s = 'mona_lisa';
      else if (/^Moonwalk —/.test(w.title)) s = 'moonwalk';
      else if (/^Beethoven —/.test(w.title)) s = 'beethoven';
    }
    if (!s) return acc;
    (acc[s] = acc[s] || []).push(w);
    return acc;
  }, {});

  // Within each series: portfolio card always last, and individual prints
  // sorted by their II.xx reference number so 11.22 → 11.31 / 11.64 → 11.73
  // read in canonical Feldman-Schellmann order. Falls back to `createdAt`
  // for series without an II.xx convention (Mona Lisa, Beethoven, etc.).
  Object.keys(seriesGroups).forEach((key) => {
    seriesGroups[key].sort((a, b) => {
      const isPortfolio = (w) => /portfolio|complete|\bset\b/i.test(w.title || '');
      const pa = isPortfolio(a) ? 1 : 0;
      const pb = isPortfolio(b) ? 1 : 0;
      if (pa !== pb) return pa - pb;  // portfolio cards always at the end
      const refNum = (w) => {
        const m = (w.title || '').match(/\b1?[1I]\.(\d{1,3})\b/);
        return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
      };
      const na = refNum(a);
      const nb = refNum(b);
      if (na !== nb) return na - nb;
      const stamp = (w) => w.createdAt || w.updatedAt || '';
      return (stamp(a) || '').localeCompare(stamp(b) || '');
    });
  });

  // Ordered list of sections to render (matches Gabrielle's spec sheet order).
  const SECTIONS = [
    { key: 'marilyn_classic', title: 'Marilyn',                kicker: 'Open Edition · £450 each · 10-Print Portfolio £3,750 · 36 × 36 in.' },
    { key: 'flowers',         title: 'Flowers',                kicker: 'Open Edition · £450 each · 36 × 36 in.' },
    { key: 'soup_one',        title: "Campbell's Soup Cans — Series I (1968)", kicker: 'Open Edition · £400 each · 10-Print Portfolio £3,500 · 35 × 23 in.' },
    { key: 'mao',             title: 'Mao',                    kicker: 'Open Edition · £450 each · 5-Print Portfolio £2,000 · 33½ × 29½ in.' },
    { key: 'golden_marilyn',  title: 'Golden Marilyn',         kicker: 'Limited Edition of 2,000 · £750 each · 10-Print Portfolio £5,000 · 36 × 36 in.' },
    { key: 'diamond_dust',    title: 'Diamond Dust Marilyn',   kicker: 'Limited Edition of 2,500 · £2,000 · 36 × 36 in.' },
    { key: 'kiss',            title: 'I Love Your Kiss Forever Forever', kicker: 'Limited Edition of 2,500 · £750 · 16½ × 22½ in. on vellum' },
    { key: 'dollar_signs',    title: 'Dollar Signs',           kicker: 'Limited Edition of 1,000 · £450 each · 4-Print Portfolio £5,000 · 15⅝ × 19¾ in.' },
    { key: 'soup_two',        title: "Campbell's Soup Cans — Series II (1969)", kicker: 'Limited Edition of 1,500 portfolios · £7,500 (10-print portfolio only) · 35 × 23 in.' },
    { key: 'scream',          title: 'The Scream (after Edvard Munch)', kicker: 'Limited Edition of 1,500 · £750 each · 3-Piece Set £2,000 · 25 × 35 in.' },
    { key: 'mona_lisa',       title: 'Mona Lisa (after Leonardo da Vinci)', kicker: 'Limited Edition of 2,500 · £750 each · five variations · 25 × 35 in.' },
    { key: 'moonwalk',        title: 'Moonwalk',               kicker: "Collector's Edition of 3,000 · £2,750 each · 38½ × 38½ in." },
    { key: 'beethoven',       title: 'Beethoven',              kicker: "Collector's Edition of 3,000 · £1,250 each · 38½ × 38½ in." },
  ];

  const heroImage = works.find((w) => w.title?.includes('Diamond Dust'))?.imageUrl
    || works.find((w) => w.title?.includes('Golden Marilyn'))?.imageUrl
    || works[0]?.imageUrl;
  const heroSrc = heroImage?.startsWith('http') ? heroImage : (heroImage ? `${API_URL}${heroImage}` : null);

  return (
    <div className="min-h-screen bg-white" data-testid="sunday-b-morning-page">
      <SEO
        title="Sunday B. Morning — After Warhol | Zebra One"
        description="Sunday B. Morning silkscreens after Andy Warhol — Marilyn, Flowers, Soup Cans, Mao, Dollar Signs, Golden Marilyn, Diamond Dust Marilyn, Moonwalk and Beethoven. Printed in Belgium using Warhol's original photo negatives. Recognised in the Andy Warhol Catalogue Raisonné."
        canonical={typeof window !== 'undefined' ? window.location.origin + '/sunday-b-morning' : undefined}
        image={heroSrc}
      />
      <Header />

      {/* ─────────────────────────── Hero ─────────────────────────── */}
      <motion.section
        ref={heroRef}
        style={{ opacity, scale }}
        className="relative min-h-[85vh] flex items-center justify-center overflow-hidden border-b border-neutral-200 bg-black"
      >
        {heroSrc && (
          <div className="absolute inset-0">
            <img
              src={heroSrc}
              alt="Sunday B. Morning — Diamond Dust Marilyn"
              className="w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/80" />
          </div>
        )}

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="inline-block px-4 py-2 border border-white/30 text-white text-xs tracking-[0.3em] uppercase backdrop-blur-sm bg-white/10">
              After Andy Warhol · Published in Belgium since 1970
            </div>
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light tracking-tight text-white">
              Sunday B. Morning
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-3xl mx-auto font-light leading-relaxed">
              The Belgian silkscreens Warhol himself helped set in motion — printed from
              his original photo negatives and stamped <em>"fill in your own signature"</em>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <motion.button
                whileHover={HOVER_LIFT}
                whileTap={TAP_PRESS}
                onClick={() => document.getElementById('sbm-classic')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black hover:bg-white/90 transition-colors text-sm font-medium tracking-wider"
                data-testid="sbm-hero-shop-cta"
              >
                <Sparkles className="w-4 h-4" />
                Browse Editions
                <ArrowRight className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={HOVER_LIFT}
                whileTap={TAP_PRESS}
                onClick={() => document.getElementById('sbm-history')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-black transition-colors text-sm font-medium tracking-wider backdrop-blur-sm"
                data-testid="sbm-hero-story-cta"
              >
                <BookOpen className="w-4 h-4" />
                The Story
              </motion.button>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* ─────────────────────────── Storyline ─────────────────────────── */}
      <Section id="sbm-history" className="py-24 sm:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 prose prose-neutral prose-lg">
          <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">A Brief History</div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight !mt-0 mb-10">
            The strangest, most important Warhol prints ever made.
          </h2>
          <p className="text-lg text-neutral-700 leading-relaxed">
            The origin of the name <em>Sunday B. Morning</em> remains a mystery. It is rumoured
            to have derived from <em>Sunday Belgian Morning</em>, but who really knows? Much of
            the workshop's earliest history is lost.
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            What is known is that, after publishing his celebrated <em>Factory Editions</em> of
            Marilyn, Flowers and the Campbell's Soup Cans, in 1970 Andy Warhol began collaborating
            with two anonymous friends in Belgium on a second series of prints. The original idea
            was to play on the concept of mass production — a phenomenon Warhol returned to
            obsessively. The verso stamp <strong>"fill in your own signature"</strong> was his
            joke about the collapse of authorship: <em>"here we just mass-produced these prints;
            sign your name. Any name will do. Yours is as important as my own."</em> The new prints
            were exact in detail to the Factory editions, and Warhol was, in effect, mocking the
            idea that the Factory editions were somehow more important than these.
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            At some point talks broke down. Maybe Warhol had second thoughts about how the Belgian
            project might affect the market for his Factory editions. By that time he had already
            handed over the photo negatives and the colour codes used to produce the prints, and
            the Belgians had taken them to begin printing.
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            And print they did. They published editions of 250 each of Marilyn, Flowers, Campbell's
            Soup Cans and Campbell's Soup Cans II — produced with exactly the same tools and
            methods Warhol himself had used. These early black-ink editions are now noted in
            Feldman & Schellmann's <em>Andy Warhol Prints: A Catalogue Raisonné 1962–1987</em>.
            Page 173 reads: <em>"This is an unauthorised print 36 x 36 in. (91.4 cm) in an edition
            of 250 with a stamped number. Stamped in black ink on verso 'fill in your own
            signature' and published by Sunday B. Morning."</em>
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            Warhol was unhappy with the project, but unable to stop it: he had already supplied
            the means. So whenever he ran across a Sunday B. Morning print, he would sign it
            <strong> "this is not by me — Andy Warhol"</strong>. This of course only made the
            prints more sought-after, especially the ones he signed in defiance.
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            Today, the original 1970 black-ink Sunday B. Mornings are very rare; many did not
            survive. In the late 1990s the workshop began publishing again — now stamping the
            verso in blue ink — and continues to publish the Marilyns, Flowers, Soup Cans and
            Maos alongside a programme of newer limited editions: Golden Marilyn, Diamond Dust
            Marilyn, Dollar Signs, the Scream after Munch, the Mona Lisa after Leonardo, Moonwalk
            and Beethoven. Ownership of the workshop has changed hands a couple of times, but the
            prints are still made by the same Belgian print shop, using the same processes, with
            the same tools Warhol himself originally supplied. No other publisher comes close.
          </p>

          <div className="not-prose grid grid-cols-1 sm:grid-cols-3 gap-8 mt-16 mb-12">
            <div className="border-l-2 border-black pl-5">
              <Stamp className="w-6 h-6 mb-3" />
              <p className="font-medium mb-1">Verso Stamps</p>
              <p className="text-sm text-neutral-600 leading-relaxed">Each print stamped <em>"published by Sunday B. Morning"</em> and <em>"fill in your own signature"</em>.</p>
            </div>
            <div className="border-l-2 border-black pl-5">
              <BookOpen className="w-6 h-6 mb-3" />
              <p className="font-medium mb-1">Catalogue Raisonné</p>
              <p className="text-sm text-neutral-600 leading-relaxed">Recognised in Feldman & Schellmann's <em>Andy Warhol Prints, 1962–1987</em>, p. 173.</p>
            </div>
            <div className="border-l-2 border-black pl-5">
              <Layers className="w-6 h-6 mb-3" />
              <p className="font-medium mb-1">Belgian Printshop</p>
              <p className="text-sm text-neutral-600 leading-relaxed">All editions printed at the original 1970 workshop in Belgium, on museum board, using Warhol's photo negatives.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* ─────────────────────────── Catalogue ─────────────────────────── */}
      {loading ? (
        <Section className="py-32 text-center text-neutral-500" data-testid="sbm-loading">
          <div className="inline-block w-8 h-8 border-2 border-neutral-300 border-t-black rounded-full animate-spin" />
          <p className="mt-4 text-sm">Loading catalogue…</p>
        </Section>
      ) : (
        <>
          <SeriesNav sections={SECTIONS} seriesGroups={seriesGroups} />
          <div id="sbm-classic" />
          {SECTIONS.map((s) => (
            <SeriesGrid
              key={s.key}
              id={`sbm-${s.key.replace(/_/g, '-')}`}
              title={s.title}
              kicker={s.kicker}
              works={seriesGroups[s.key] || []}
              seriesRatio={SERIES_RATIO[s.key] || 'aspect-square'}
              seriesFrame={SERIES_FRAME[s.key] || DEFAULT_FRAME}
              navigate={navigate}
            />
          ))}
        </>
      )}

      {/* ─────────────────────────── Framing & Provenance ─────────────────────────── */}
      <Section className="py-24 sm:py-32 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-16">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">Framing</div>
            <h3 className="text-3xl sm:text-4xl font-light tracking-tight mb-6">In the spirit of 1970.</h3>
            <p className="text-neutral-700 leading-relaxed mb-4">
              At Zebra One we offer a bespoke framing service inspired by the way the original
              1970 Sunday B. Morning editions were presented: encased in a beveled <span className="whitespace-nowrap">⅛-inch</span> UV-protected
              plexiglass face, with linen-wrapped sides and the print resting on PH-neutral paper.
            </p>
            <p className="text-neutral-700 leading-relaxed">
              Because of the encased construction the verso stamps are not visible through windows —
              but documentation photographs are supplied with every framed work, alongside a
              certificate citing the impression's place in the Catalogue Raisonné.
            </p>
          </div>
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">Provenance</div>
            <h3 className="text-3xl sm:text-4xl font-light tracking-tight mb-6">Why these prints, and not the imitations?</h3>
            <p className="text-neutral-700 leading-relaxed mb-4">
              Many publishers have tried to reproduce these images. None come close. Only Sunday
              B. Morning possesses the photo negatives Warhol himself supplied — the precise tools
              that produced the Factory editions.
            </p>
            <p className="text-neutral-700 leading-relaxed">
              Why Warhol never made any further attempt to challenge their use of his work
              remains, like the workshop's name, a mystery. The result, sixty years on, is one of
              the most singular and authentic continuations of any 20th-century artist's printed
              œuvre.
            </p>
          </div>
        </div>
      </Section>

      {/* ─────────────────────────── CTA ─────────────────────────── */}
      <Section className="py-24 sm:py-32 bg-black text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto mb-6 text-white/70" />
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-6">
            Enquire about a Sunday B. Morning.
          </h2>
          <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto mb-10">
            Bespoke shipping is offered worldwide. For framing options, edition numbers and
            condition photographs of any specific work, please get in touch.
          </p>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => navigate('/contact?subject=Sunday%20B.%20Morning%20Enquiry')}
            className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black hover:bg-neutral-100 transition-colors text-sm uppercase tracking-[0.2em] font-medium"
            data-testid="sbm-enquire-cta"
          >
            Make an Enquiry
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </div>
      </Section>

      <Footer />
    </div>
  );
};

export default SundayBMorningPage;
